// Change detection for post-ready dispatch edits.
//
// The shipper can re-open the dispatch builder after it is already marked
// ready and edit addresses, contacts, reference numbers or instructions. The
// broker needs to be told — but ONLY when something actually changed, so an
// open-and-save with no edits stays silent.
//
// Pure functions only: build a normalised snapshot of the shipper-editable
// fields, then diff two snapshots into human-readable field labels.

export interface DispatchSnapshotSource {
  pallets: number | null;
  weight: number | null;
  commodity: string | null;
  notes: string | null;
  stops: Array<{
    stop_type?: string | null;
    name?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    scheduled_date?: string | null;
    scheduled_time_start?: string | null;
    scheduled_time_end?: string | null;
    contact_name?: string | null;
    contact_phone?: string | null;
    contact_email?: string | null;
    reference_number?: string | null;
    pallet_count?: number | null;
    piece_count?: number | null;
    weight_lbs?: number | null;
    instructions?: string | null;
  }>;
}

/** Normalised, order-stable map of field-path → value. */
export type DispatchSnapshot = Record<string, string>;

function norm(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "";
  return String(v).trim();
}

const STOP_FIELDS: Array<[key: string, label: string]> = [
  ["stop_type", "type"],
  ["name", "location name"],
  ["address", "address"],
  ["city", "city"],
  ["state", "state"],
  ["postal_code", "ZIP"],
  ["scheduled_date", "date"],
  ["scheduled_time_start", "time window"],
  ["scheduled_time_end", "time window"],
  ["contact_name", "contact name"],
  ["contact_phone", "contact phone"],
  ["contact_email", "contact email"],
  ["reference_number", "reference number"],
  ["pallet_count", "pallets"],
  ["piece_count", "pieces"],
  ["weight_lbs", "weight"],
  ["instructions", "instructions"],
];

const LOAD_FIELDS: Array<[key: keyof DispatchSnapshotSource, label: string]> = [
  ["pallets", "load pallets"],
  ["weight", "load weight"],
  ["commodity", "commodity"],
  ["notes", "confidential notes"],
];

export function buildDispatchSnapshot(src: DispatchSnapshotSource): DispatchSnapshot {
  const snap: DispatchSnapshot = {};
  for (const [key] of LOAD_FIELDS) snap[`load.${String(key)}`] = norm(src[key] as unknown);
  src.stops.forEach((s, i) => {
    for (const [key] of STOP_FIELDS) {
      snap[`stop.${i + 1}.${key}`] = norm((s as Record<string, unknown>)[key]);
    }
  });
  snap["stop.count"] = String(src.stops.length);
  return snap;
}

/**
 * Diff two snapshots into a de-duplicated list of human-readable labels,
 * e.g. ["stop 1 address", "stop 2 contact phone", "commodity"].
 */
export function diffDispatchSnapshots(before: DispatchSnapshot, after: DispatchSnapshot): string[] {
  const labels: string[] = [];
  const seen = new Set<string>();
  const push = (label: string) => {
    if (seen.has(label)) return;
    seen.add(label);
    labels.push(label);
  };

  if (before["stop.count"] !== after["stop.count"]) push("stops added or removed");

  for (const [key, label] of LOAD_FIELDS) {
    const k = `load.${String(key)}`;
    if (norm(before[k]) !== norm(after[k])) push(label);
  }

  const stopCount = Math.max(
    Number(before["stop.count"] ?? 0) || 0,
    Number(after["stop.count"] ?? 0) || 0,
  );
  for (let i = 1; i <= stopCount; i++) {
    for (const [key, label] of STOP_FIELDS) {
      const k = `stop.${i}.${key}`;
      if (norm(before[k]) !== norm(after[k])) push(`stop ${i} ${label}`);
    }
  }

  return labels;
}

/** Short, human summary for a notification body: "stop 1 address, commodity +2 more". */
export function summarizeChanges(labels: string[], max = 3): string {
  if (labels.length === 0) return "";
  const head = labels.slice(0, max).join(", ");
  const rest = labels.length - Math.min(max, labels.length);
  return rest > 0 ? `${head} +${rest} more` : head;
}
